/**
 * Bundle 171 hotfix - Admin Golden Tickets tab.
 *
 * Rendered from AdminEventEditPage under a new "Golden Tickets" tab.
 * Reads/writes the Bundle 171 backend routes at /admin/events/{id}/golden-tickets.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Copy, RefreshCcw, Plus, Ban, PenLine } from 'lucide-react';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';

const fmtDiscount = (t) => (
  t.discount_type === 'percentage'
    ? `${t.discount_value}% off`
    : `$${(t.discount_value / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })} off`);

const validRangeLabel = (t) => {
  if (!t.valid_until) return 'Auto-expires at event end';
  const to = new Date(t.valid_until);
  return `Until ${to.toLocaleDateString()}`;
};

const emptyCreate = {
  discount_type: 'fixed_amount', discount_value: 1000,
  max_total_uses: 50, valid_from: '', valid_until: '',
  code: '', notes: '', autogen: true,
};

const CreateModal = ({ open, onClose, onSaved, eventId, bulk }) => {
  const [form, setForm] = useState(emptyCreate);
  const [count, setCount] = useState(50);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(emptyCreate);
      setCount(50);
      setError('');
      setResult(null);
    }
  }, [open]);

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const body = {
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
      };
      if (form.max_total_uses) body.max_total_uses = Number(form.max_total_uses);
      if (form.valid_from) body.valid_from = new Date(form.valid_from).toISOString();
      if (form.valid_until) body.valid_until = new Date(form.valid_until).toISOString();
      if (form.notes) body.notes = form.notes;
      if (bulk) {
        body.count = Math.max(1, Math.min(200, Number(count)));
        const { data } = await api.post(
          `/admin/events/${eventId}/golden-tickets/bulk`, body);
        setResult(data);
        toast.success(`Generated ${data.count} codes`);
        if (typeof onSaved === 'function') onSaved();
      } else {
        if (!form.autogen && form.code) body.code = form.code.trim().toUpperCase();
        await api.post(`/admin/events/${eventId}/golden-tickets`, body);
        toast.success('Code created');
        if (typeof onSaved === 'function') onSaved();
        onClose();
      }
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not create code'));
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = () => {
    if (!result || !result.codes) return;
    const rows = [['code'], ...result.codes.map((c) => [c.code])];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `golden-tickets-${eventId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid={bulk ? 'gt-bulk-modal' : 'gt-create-modal'}
      >
        <DialogHeader>
          <DialogTitle>
            {bulk ? 'Bulk generate golden tickets' : 'Create a golden ticket'}
          </DialogTitle>
          <DialogDescription>
            {bulk
              ? 'For printed tickets. Codes are auto-generated per row.'
              : 'Codes are event-scoped and 8 uppercase alphanumeric chars.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="flex gap-3">
            <label
              className="flex items-center gap-2"
              data-testid="gt-discount-type-fixed"
            >
              <input
                type="radio" name="dtype" value="fixed_amount"
                checked={form.discount_type === 'fixed_amount'}
                onChange={() => setForm({ ...form, discount_type: 'fixed_amount' })}
              />
              $ off
            </label>
            <label
              className="flex items-center gap-2"
              data-testid="gt-discount-type-pct"
            >
              <input
                type="radio" name="dtype" value="percentage"
                checked={form.discount_type === 'percentage'}
                onChange={() => setForm({ ...form, discount_type: 'percentage' })}
              />
              % off
            </label>
          </div>
          <div>
            <Label htmlFor="gt-value">
              {form.discount_type === 'percentage'
                ? 'Percent (1-100)'
                : 'Amount in cents (e.g. 1000 = $10)'}
            </Label>
            <Input
              id="gt-value" type="number"
              min={1}
              max={form.discount_type === 'percentage' ? 100 : undefined}
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              data-testid="gt-discount-value"
            />
          </div>
          {bulk ? (
            <div>
              <Label htmlFor="gt-count">Number of codes (1-200)</Label>
              <Input
                id="gt-count" type="number" min={1} max={200}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                data-testid="gt-bulk-count"
              />
            </div>
          ) : null}
          <div>
            <Label htmlFor="gt-max">Max total uses (blank = unlimited)</Label>
            <Input
              id="gt-max" type="number" min={1}
              value={form.max_total_uses}
              onChange={(e) => setForm({ ...form, max_total_uses: e.target.value })}
              data-testid="gt-max-uses"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="gt-from">Valid from</Label>
              <Input
                id="gt-from" type="datetime-local"
                value={form.valid_from}
                onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="gt-until">Valid until</Label>
              <Input
                id="gt-until" type="datetime-local"
                value={form.valid_until}
                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              />
            </div>
          </div>
          <div className="text-xs text-neutral-500">
            Leave both blank to auto-expire when the event ends.
          </div>
          {!bulk ? (
            <div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox" checked={form.autogen}
                  onChange={(e) => setForm({ ...form, autogen: e.target.checked, code: '' })}
                  data-testid="gt-autogen-toggle"
                />
                Auto-generate code
              </label>
              {!form.autogen ? (
                <Input
                  className="mt-1 font-mono uppercase"
                  placeholder="8 uppercase alphanumeric chars"
                  maxLength={8}
                  value={form.code}
                  onChange={(e) => setForm({
                    ...form,
                    code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                  })}
                  data-testid="gt-custom-code"
                />
              ) : null}
            </div>
          ) : null}
          <div>
            <Label htmlFor="gt-notes">Notes (private)</Label>
            <Textarea
              id="gt-notes" rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="gt-notes"
            />
          </div>
          {error ? (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
              data-testid="gt-modal-error"
            >{error}</div>
          ) : null}
          {result && result.codes ? (
            <div className="border border-neutral-200 rounded p-2 space-y-1 max-h-40 overflow-y-auto">
              <div className="text-xs text-neutral-600 font-semibold">
                Generated {result.count} codes:
              </div>
              {result.codes.map((c) => (
                <div key={c.id} className="text-xs font-mono">{c.code}</div>
              ))}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          {result && bulk ? (
            <Button
              variant="outline" size="sm" onClick={downloadCsv}
              data-testid="gt-bulk-csv"
            >
              Download CSV
            </Button>
          ) : null}
          <Button
            variant="outline" size="sm" onClick={onClose}
            data-testid="gt-modal-cancel"
          >Close</Button>
          {!result ? (
            <Button
              size="sm" onClick={submit} disabled={busy}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="gt-modal-submit"
            >
              {busy ? 'Saving...' : (bulk ? 'Generate' : 'Create')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GoldenTicketsPanel = ({ eventId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Bundle 171 hotfix C3 - surface when an event has no active paid
  // tier so admins know minted codes will never apply. The Golden
  // Ticket toggle only renders inside the paid-flow markup on
  // PaymentStep, so a $0 or missing tier means the RSVP wizard
  // never reaches the surface where codes are redeemed.
  const [tierProbe, setTierProbe] = useState({ loading: true, activeTier: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(
        `/admin/events/${eventId}/golden-tickets`);
      setRows(data.golden_tickets || []);
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not load codes'));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // Bundle 171 hotfix C3 - independent tier probe. Uses the same
  // public endpoint the RSVP wizard hits so the "not applicable"
  // signal matches what a real guest would see.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/payments/methods/${eventId}`);
        if (!cancelled) {
          setTierProbe({ loading: false, activeTier: data.active_tier || null });
        }
      } catch (_) {
        if (!cancelled) {
          setTierProbe({ loading: false, activeTier: null });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const noPaidTier = (
    !tierProbe.loading
    && (!tierProbe.activeTier
        || (tierProbe.activeTier.price_cents ?? 0) === 0));

  const deactivate = async (id) => {
    try {
      await api.patch(`/admin/golden-tickets/${id}`,
        { is_active: false });
      toast.success('Code deactivated');
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not deactivate'));
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  const active = useMemo(() => rows.filter((r) => r.is_active !== false),
    [rows]);
  const inactive = useMemo(() => rows.filter((r) => r.is_active === false),
    [rows]);

  return (
    <div className="space-y-4" data-testid="golden-tickets-panel">
      {noPaidTier ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          role="status"
          data-testid="gt-no-paid-tier-notice"
        >
          <div className="font-semibold">
            Golden ticket entries are not applicable.
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            This event has no active paid tier. Add or activate a tier
            to enable discount codes.
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-primary-900">
            Golden Tickets
          </h3>
          <p className="text-xs text-neutral-500">
            Event-scoped voucher codes. Redeemable once per user; no
            stacking; auto-expire at event end unless dated.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={load} disabled={loading}
            data-testid="gt-refresh"
          >
            <RefreshCcw
              className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setBulkOpen(true)}
            data-testid="gt-bulk-open"
          >
            Bulk generate
          </Button>
          <Button
            size="sm"
            className="bg-primary-900 text-white hover:bg-primary-800"
            onClick={() => setCreateOpen(true)}
            data-testid="gt-create-open"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create code
          </Button>
        </div>
      </div>
      {error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      ) : null}
      <div className="border border-neutral-200 rounded overflow-x-auto">
        <table className="w-full text-sm" data-testid="gt-table">
          <thead className="bg-neutral-50">
            <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
              <th className="text-left py-2 px-3">Code</th>
              <th className="text-left py-2 px-3">Discount</th>
              <th className="text-left py-2 px-3">Valid</th>
              <th className="text-left py-2 px-3">Uses</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {active.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-neutral-500 text-xs">
                  No golden tickets yet. Create one to get started.
                </td>
              </tr>
            ) : null}
            {active.map((t) => (
              <tr
                key={t.id}
                className="border-t border-neutral-100"
                data-testid={`gt-row-${t.code}`}
              >
                <td className="py-2 px-3 font-mono">
                  <button
                    type="button" onClick={() => copy(t.code)}
                    className="flex items-center gap-1 hover:text-primary-800"
                    data-testid={`gt-copy-${t.code}`}
                  >
                    {t.code}
                    <Copy className="h-3 w-3 opacity-50" />
                  </button>
                </td>
                <td className="py-2 px-3">{fmtDiscount(t)}</td>
                <td className="py-2 px-3 text-xs text-neutral-600">
                  {validRangeLabel(t)}
                </td>
                <td
                  className="py-2 px-3 font-mono text-xs"
                  data-testid={`gt-uses-${t.code}`}
                >
                  {t.uses_count} / {t.max_total_uses ?? 'unlimited'}
                </td>
                <td className="py-2 px-3 text-right">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deactivate(t.id)}
                    className="text-red-700 hover:bg-red-50"
                    data-testid={`gt-deactivate-${t.code}`}
                  >
                    <Ban className="h-3 w-3 mr-1" /> Deactivate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {inactive.length > 0 ? (
        <details
          className="border border-neutral-100 rounded p-2"
          data-testid="gt-deactivated-section"
        >
          <summary className="text-xs text-neutral-500 cursor-pointer">
            Deactivated ({inactive.length})
          </summary>
          <table className="w-full text-xs mt-2">
            <tbody>
              {inactive.map((t) => (
                <tr key={t.id} className="text-neutral-500">
                  <td className="py-1 px-2 font-mono">{t.code}</td>
                  <td className="py-1 px-2">{fmtDiscount(t)}</td>
                  <td className="py-1 px-2">{t.uses_count} used</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
      <CreateModal
        open={createOpen} onClose={() => setCreateOpen(false)}
        onSaved={load} eventId={eventId} bulk={false}
      />
      <CreateModal
        open={bulkOpen} onClose={() => setBulkOpen(false)}
        onSaved={load} eventId={eventId} bulk={true}
      />
    </div>
  );
};

export default GoldenTicketsPanel;
